import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Globe, Pencil, Upload, X } from 'lucide-react';
import { DynamicIcon, iconNames } from 'lucide-react/dynamic';
import { getHostedAppUrl } from '../../appMode/hostConfig';
import type {
  FabrikAppDefinition,
  FabrikResolvedAppearance,
  FabrikAppLogo,
  FabrikLogoStyle,
} from '../../types/apps';
import {
  getFabrikAppAppearance,
  updateFabrikAppDraft,
  uploadFabrikAppLogo,
  validateFabrikAppLogoFile,
} from '../../services/appsApi';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea, ScrollAreaContent, ScrollAreaViewport, ScrollBar } from '../ui/scroll-area';
import { Textarea } from '../ui/textarea';
import { ToastList, ToastProvider, ToastViewport, createToastManager } from '../ui/toast';
import AppBrandMark from '../AppRenderer/AppBrandMark';
import FabrikTheme from '../AppRenderer/FabrikTheme';
import ManagementNameSettings from './ManagementNameSettings';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

type Props = {
  tenantId: string;
  appId: string;
  tenantName: string;
  definition?: FabrikAppDefinition;
  appearance?: FabrikResolvedAppearance;
  version: 'draft' | 'published';
  onDefinitionChange: (definition: FabrikAppDefinition) => void;
  onRenameWorkspace: (name: string, preserveOldUrl: boolean) => Promise<void>;
  canonicalWorkspaceName: string;
  appSlug: string;
  isAppPublished: boolean;
};

const DEFAULT_LOGO_COLOR = '#42586e';
const hexColor = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value);
const slugify = (value: string, maxLength = 72) => {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, maxLength);

  return slug || 'app';
};
const appSlugForName = (name: string, tenantName: string) => {
  const tenantSlug = slugify(tenantName, 24);
  return `${slugify(name, 55 - tenantSlug.length)}-${tenantSlug}`;
};
const colorPalette = [
  '#42586e',
  '#e94855',
  '#f76551',
  '#f49a3d',
  '#e7be18',
  '#21b86b',
  '#0dbda8',
  '#61bfdf',
  '#2587ed',
  '#8253be',
  '#e95eb6',
];
const logoStyles: { id: FabrikLogoStyle; label: string }[] = [
  { id: 'filled', label: 'Filled' },
  { id: 'filled-circle', label: 'Filled circle' },
  { id: 'rounded', label: 'Rounded' },
  { id: 'outline', label: 'Outline' },
  { id: 'circle', label: 'Circle' },
  { id: 'square', label: 'Square' },
  { id: 'serif', label: 'Serif' },
  { id: 'mono', label: 'Mono' },
];

function CurrentColorSwatch({
  value,
  label,
  compact = false,
}: {
  value: string;
  label: string;
  compact?: boolean;
}) {
  return (
    <span
      role="img"
      aria-label={label}
      className={`grid ${
        compact ? 'h-11 w-12' : 'h-14 w-16'
      } shrink-0 place-items-center rounded-lg border border-app-border p-1`}
    >
      <span
        className="logo-color-swatch size-full rounded-md"
        style={hexColor(value) ? ({ '--logo-color-swatch': value } as CSSProperties) : undefined}
      />
    </span>
  );
}

function ColorPalette({
  value,
  onChange,
  compact = false,
}: {
  value: string;
  onChange: (color: string) => void;
  compact?: boolean;
}) {
  return (
    <div
      className={`flex flex-wrap ${compact ? 'mt-3 gap-2' : 'mt-5 gap-3'}`}
      role="group"
      aria-label="Color palette"
    >
      {colorPalette.map((color) => (
        <button
          key={color}
          type="button"
          aria-label={`Choose color ${color}`}
          aria-pressed={value.toLowerCase() === color}
          onClick={() => onChange(color)}
          className={`logo-color-swatch ${
            compact ? 'size-9' : 'size-11'
          } rounded-full transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
            value.toLowerCase() === color ? 'ring-2 ring-ring ring-offset-2' : ''
          }`}
          style={{ '--logo-color-swatch': color } as CSSProperties}
        />
      ))}
    </div>
  );
}

/** Shows save confirmations inside the settings section instead of the app-wide toaster. */
const settingsToast = createToastManager();

export default function AppearanceSettings({
  tenantId,
  appId,
  tenantName,
  definition,
  appearance,
  version,
  onDefinitionChange,
  onRenameWorkspace,
  canonicalWorkspaceName,
  appSlug,
  isAppPublished,
}: Props) {
  const [saving, setSaving] = useState(false);
  const [resolvedAppearance, setResolvedAppearance] = useState(appearance);
  const [tenantAppearance, setTenantAppearance] = useState<FabrikResolvedAppearance>();
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [preserveOldUrl, setPreserveOldUrl] = useState(false);
  const [logoMode, setLogoMode] = useState<'typography' | 'icon' | 'custom'>('typography');
  const [logoSearch, setLogoSearch] = useState('');
  const [logo, setLogo] = useState<FabrikAppLogo>();
  const [upload, setUpload] = useState<File>();
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState('');
  const [hex, setHex] = useState('');
  const [logoColor, setLogoColor] = useState(DEFAULT_LOGO_COLOR);
  const [appName, setAppName] = useState('');
  const [canonicalAppName, setCanonicalAppName] = useState(canonicalWorkspaceName);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!dialogOpen) return;
    const viewport = document
      .getElementById('fabrik-settings-panel')
      ?.querySelector<HTMLElement>('[data-slot="scroll-area-viewport"]');
    if (!viewport) return;
    const previousOverflowY = viewport.style.overflowY;
    viewport.style.overflowY = 'hidden';
    return () => {
      viewport.style.overflowY = previousOverflowY;
    };
  }, [dialogOpen]);

  useEffect(() => {
    const controller = new AbortController();
    getFabrikAppAppearance(tenantId, appId, controller.signal)
      .then((result) => {
        setTenantAppearance(result.tenant);
        setResolvedAppearance(result.effective);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [appId, tenantId]);

  useEffect(() => {
    setHex(definition?.theme?.primaryColor || resolvedAppearance?.primaryColor || '#0D6B8A');
    setLogoColor(definition?.theme?.logo?.color || DEFAULT_LOGO_COLOR);
    setAppName(definition?.app.name || '');
    setDescription(definition?.app.description || '');
  }, [definition, resolvedAppearance]);

  useEffect(() => setCanonicalAppName(canonicalWorkspaceName), [canonicalWorkspaceName]);

  const save = async (next: FabrikAppDefinition, file?: File) => {
    setSaving(true);
    setError('');
    try {
      let theme = next.theme;
      if (file) {
        const { logoKey } = await uploadFabrikAppLogo(tenantId, appId, file);
        theme = {
          ...theme,
          logo: {
            type: 'custom',
            key: logoKey,
            color: hexColor(logoColor) ? logoColor : theme?.logo?.color,
          },
        };
        next = { ...next, theme };
      }
      const result = await updateFabrikAppDraft(tenantId, appId, next);
      onDefinitionChange(result.draft_definition || next);
      getFabrikAppAppearance(tenantId, appId)
        .then((appearanceResult) => {
          setTenantAppearance(appearanceResult.tenant);
          setResolvedAppearance(appearanceResult.effective);
        })
        .catch(() => undefined);
      return true;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to save appearance.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveText = (value: string) => {
    if (!definition || version !== 'draft') return;
    const next = { ...definition, app: { ...definition.app, name: value } };
    onDefinitionChange(next);
    void save(next).then((saved) => {
      if (saved) settingsToast.add({ type: 'success', title: 'Display name saved' });
    });
  };

  const saveAppDetails = async () => {
    if (!definition || !hexColor(hex) || !canonicalAppName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const primaryColorChanged =
        hexColor(hex) &&
        hex !== (definition.theme?.primaryColor || resolvedAppearance?.primaryColor);
      const next = {
        ...definition,
        app: { ...definition.app, description: description || undefined },
        theme: {
          ...definition.theme,
          ...(primaryColorChanged ? { primaryColor: hex } : {}),
        },
      };
      const result = await updateFabrikAppDraft(tenantId, appId, next);
      onDefinitionChange(result.draft_definition || next);
      const renamed = canonicalAppName.trim() !== canonicalWorkspaceName;
      if (renamed) {
        await onRenameWorkspace(canonicalAppName.trim(), preserveOldUrl);
      }
      setDetailsDialogOpen(false);
      settingsToast.add({
        type: 'success',
        title: renamed ? 'App renamed and details saved' : 'App details saved',
      });
      getFabrikAppAppearance(tenantId, appId)
        .then((appearanceResult) => {
          setTenantAppearance(appearanceResult.tenant);
          setResolvedAppearance(appearanceResult.effective);
        })
        .catch(() => undefined);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to save app details.');
    } finally {
      setSaving(false);
    }
  };

  const theme = definition?.theme;
  const activeLogo = dialogOpen ? logo : definition?.theme?.logo;
  const activeLogoStyle =
    activeLogo?.type === 'typography'
      ? activeLogo.style === 'default' || !activeLogo.style
        ? 'filled'
        : activeLogo.style
      : undefined;
  const previewAppearance: FabrikResolvedAppearance = {
    displayName: resolvedAppearance?.displayName || tenantName,
    primaryColor: hexColor(hex) ? hex : resolvedAppearance?.primaryColor || '#0D6B8A',
    logo: uploadPreviewUrl
      ? { type: 'image', url: uploadPreviewUrl }
      : activeLogo?.type === 'icon'
      ? { type: 'icon', icon: activeLogo.icon }
      : activeLogo?.type === 'custom'
      ? resolvedAppearance?.logo || tenantAppearance?.logo || null
      : activeLogo?.type === 'typography'
      ? null
      : resolvedAppearance?.logo || tenantAppearance?.logo || null,
  };
  useEffect(() => {
    if (!upload) {
      setUploadPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(upload);
    setUploadPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [upload]);
  const beginLogoEdit = () => {
    setLogoColor(theme?.logo?.color || DEFAULT_LOGO_COLOR);
    setLogo(theme?.logo || { type: 'typography', color: DEFAULT_LOGO_COLOR, style: 'filled' });
    setUpload(undefined);
    setLogoMode(
      theme?.logo?.type === 'custom'
        ? 'custom'
        : theme?.logo?.type === 'icon'
        ? 'icon'
        : 'typography'
    );
    setLogoSearch('');
    setDialogOpen(true);
  };
  const saveLogo = async () => {
    if (!definition) return;
    const next = {
      ...definition,
      theme: {
        ...definition.theme,
        ...(logo ? { logo: { ...logo, color: hexColor(logoColor) ? logoColor : logo.color } } : {}),
      },
    };
    if (await save(next, upload)) {
      setDialogOpen(false);
      settingsToast.add({ type: 'success', title: 'Branding saved' });
    }
  };
  const changeColor = (value: string) => setHex(value);
  const changeLogoColor = (value: string) => {
    setLogoColor(value);
    setLogo((current) => (current ? { ...current, color: value } : current));
  };
  const requestSaveAppDetails = () => {
    if (isAppPublished && canonicalAppName.trim() !== canonicalWorkspaceName) {
      setPreserveOldUrl(false);
      setDetailsDialogOpen(true);
      return;
    }
    void saveAppDetails();
  };
  const appDetailsChanged =
    canonicalAppName.trim() !== canonicalWorkspaceName ||
    description !== (definition?.app.description || '') ||
    (hexColor(hex) &&
      hex !== (definition?.theme?.primaryColor || resolvedAppearance?.primaryColor));
  const currentAppSlug = appSlug || appSlugForName(canonicalWorkspaceName, tenantName);
  const newAppSlug = appSlugForName(canonicalAppName.trim(), tenantName);
  const currentAppUrl = getHostedAppUrl({ slug: currentAppSlug }) || currentAppSlug;
  const newAppUrl = getHostedAppUrl({ slug: newAppSlug }) || newAppSlug;
  const filteredIcons = iconNames
    .filter((name) => name.toLowerCase().includes(logoSearch.trim().toLowerCase()))
    .slice(0, 72);

  return (
    <ToastProvider toastManager={settingsToast} timeout={3000}>
      <div className="sticky top-0 z-50 h-0">
        <ToastViewport className="absolute top-3 left-1/2 w-72 -translate-x-1/2 sm:w-80 [&_[data-slot=toast-content]]:gap-0 [&_[data-slot=toast-content]]:px-3 [&_[data-slot=toast-content]]:py-2.5 [&_[data-slot=toast-description]]:hidden [&_[data-slot=toast-action]]:hidden">
          <ToastList />
        </ToastViewport>
      </div>
      <div className="mx-auto max-w-5xl space-y-5 p-1">
        {error && (
          <p role="alert" className="text-sm text-app-danger">
            {error}
          </p>
        )}
        {version !== 'draft' || !definition ? (
          <p className="text-sm text-app-text-secondary">
            Appearance changes are available for the draft. Live settings are read-only.
          </p>
        ) : (
          <>
            <section
              className="rounded-xl border border-app-border bg-app-surface-card p-6 sm:p-8"
              aria-labelledby="app-details-title"
            >
              <h2 id="app-details-title" className="text-xl font-semibold text-app-text">
                App details
              </h2>
              <p className="mt-2 text-base text-app-text-secondary">
                Set the app name, description, and primary color.
              </p>
              <div className="my-5 border-t border-app-border" />
              <div className="grid min-w-0 gap-5">
                <ManagementNameSettings
                  appName={canonicalAppName}
                  onNameChange={setCanonicalAppName}
                />
                <div>
                  <Label
                    htmlFor="fabrik-app-description"
                    className="text-xs font-semibold uppercase tracking-wide text-app-text-muted"
                  >
                    Description
                  </Label>
                  <Textarea
                    id="fabrik-app-description"
                    className="mt-2 min-h-11 text-base"
                    value={description}
                    maxLength={300}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                </div>
              </div>
              <div className="mt-6 border-t border-app-border pt-6">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-app-text-muted">
                  Primary color
                </h3>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <CurrentColorSwatch value={hex} label="Current primary color" />
                  <Input
                    aria-label="Primary color hex value"
                    className="h-12 w-56 max-w-full min-w-0 px-4 text-base"
                    value={hex}
                    maxLength={7}
                    onChange={(event) => setHex(event.target.value)}
                  />
                </div>
                <ColorPalette value={hex} onChange={changeColor} />
                <p className="mt-2 text-sm text-app-text-secondary">
                  Defaults to the tenant color. Used for buttons, links, and the header; logo color
                  is set in Branding.
                </p>
              </div>
              <div className="mt-6 border-t border-app-border pt-6">
                <Button
                  type="button"
                  className="h-10 px-5 bg-app-action text-app-action-foreground hover:bg-app-action-hover disabled:bg-app-border disabled:text-app-text-faint disabled:opacity-100"
                  disabled={
                    saving || !canonicalAppName.trim() || !hexColor(hex) || !appDetailsChanged
                  }
                  onClick={requestSaveAppDetails}
                >
                  Save
                </Button>
              </div>
            </section>
            <section
              className="@container min-w-0 rounded-xl border border-app-border bg-app-surface-card p-6 sm:p-8"
              aria-labelledby="brand-title"
            >
              <h2 id="brand-title" className="text-xl font-semibold text-app-text">
                Branding
              </h2>
              <p className="mt-2 text-base text-app-text-secondary">
                Set the display name and choose a logo for this app.
              </p>
              <div className="my-5 border-t border-app-border" />
              <div className="grid min-w-0 gap-6 pb-6 @lg:grid-cols-4">
                <div className="min-w-0 border-b border-app-border pb-5 @lg:col-span-1 @lg:border-b-0 @lg:border-r @lg:pb-0">
                  <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-app-text-muted">
                    Logo
                  </p>
                  <div className="relative size-20 @sm:size-28">
                    <FabrikTheme
                      className="contents"
                      theme={{ primaryColor: previewAppearance.primaryColor }}
                      syncDocument={false}
                    >
                      <AppBrandMark
                        tenantName={tenantName}
                        appearance={previewAppearance}
                        logo={theme?.logo}
                        logoColor={theme?.logo?.color || DEFAULT_LOGO_COLOR}
                        className="grid size-full place-items-center rounded-2xl bg-primary text-3xl font-semibold text-primary-foreground"
                        imageClassName="size-full rounded-2xl object-contain"
                      />
                    </FabrikTheme>
                    <Button
                      type="button"
                      variant="default"
                      size="icon"
                      aria-label="Edit logo"
                      onClick={beginLogoEdit}
                      className="absolute -bottom-1 -right-1 rounded-full shadow-md"
                    >
                      <Pencil className="size-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
                <div className="min-w-0 @lg:col-span-3">
                  <Label
                    htmlFor="fabrik-app-name"
                    className="text-xs font-semibold uppercase tracking-wide text-app-text-muted"
                  >
                    Display name
                  </Label>
                  <div className="mt-2 flex max-w-3xl items-center gap-2">
                    <Input
                      id="fabrik-app-name"
                      className="h-11 min-w-0 flex-1 text-base"
                      value={appName}
                      maxLength={80}
                      onChange={(event) => setAppName(event.target.value)}
                    />
                    <Button
                      type="button"
                      className="h-11 bg-app-action text-app-action-foreground hover:bg-app-action-hover disabled:bg-app-border disabled:text-app-text-faint disabled:opacity-100"
                      disabled={saving || !appName.trim() || appName === definition.app.name}
                      onClick={() => saveText(appName)}
                    >
                      Rename
                    </Button>
                  </div>
                  <p className="mt-2 text-sm text-app-text-secondary">
                    Shown in the app header and browser tab. The app name and URL are managed in App
                    details.
                  </p>
                </div>
              </div>
            </section>
          </>
        )}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-base leading-snug font-semibold text-app-text">
                Confirm app rename
              </DialogTitle>
              <DialogDescription className="text-app-text-muted">
                Renaming changes the app URL. If the new URL is already in use, a number may be
                added.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 text-sm">
              {[
                ['Current URL', currentAppUrl],
                ['New URL', newAppUrl],
              ].map(([label, url]) => (
                <section key={label} className="grid min-w-0 gap-1.5">
                  <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-app-text-muted">
                    {label}
                  </h3>
                  <div className="flex min-w-0 items-center gap-2 rounded-lg border border-app-border bg-app-surface-muted px-3 py-2 text-xs text-app-text-secondary">
                    <Globe className="size-3.5 shrink-0 text-app-text-muted" aria-hidden="true" />
                    <code className="min-w-0 flex-1 break-all">{url}</code>
                  </div>
                </section>
              ))}
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="preserve-old-app-url"
                checked={preserveOldUrl}
                onCheckedChange={(checked) => setPreserveOldUrl(checked === true)}
              />
              <Label
                htmlFor="preserve-old-app-url"
                className="text-sm font-normal text-app-text-secondary"
              >
                Keep the old URL as a redirect
              </Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDetailsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-app-action text-app-action-foreground hover:bg-app-action-hover disabled:bg-app-border disabled:text-app-text-faint disabled:opacity-100"
                disabled={saving}
                onClick={() => void saveAppDetails()}
              >
                {saving ? 'Saving…' : 'Rename app'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent
            container={document.getElementById('fabrik-settings-panel') || undefined}
            overlayClassName="absolute inset-0"
            showCloseButton={false}
            className="@container absolute left-1/2 top-1/2 flex h-auto max-h-full @max-3xl:max-h-3/4 w-10/12 !max-w-4xl -translate-x-1/2 -translate-y-1/2 flex-col gap-0 overflow-hidden rounded-2xl p-0"
          >
            <DialogHeader className="relative shrink-0 border-b border-app-border px-4 py-3 @sm:px-5 @sm:py-4 @lg:px-6 @lg:py-5">
              <DialogTitle className="text-xl font-medium">Edit logo</DialogTitle>
              <DialogDescription className="pr-10 text-sm">
                Customize how your app appears in the header and app list.
              </DialogDescription>
              <DialogClose
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Close dialog"
                    className="absolute right-5 top-5 sm:right-7 sm:top-7"
                  />
                }
              >
                <X aria-hidden="true" />
              </DialogClose>
            </DialogHeader>
            <FabrikTheme
              className="contents"
              theme={{ primaryColor: logoColor }}
              syncDocument={false}
            >
              <ScrollArea className="flex min-h-0 min-w-0 flex-1 flex-col">
                <ScrollAreaViewport className="h-0 min-h-0 flex-1 overscroll-contain">
                  <ScrollAreaContent className="w-full min-w-0! max-w-full">
                    <div className="grid min-w-0 gap-4 px-4 py-4 @sm:gap-5 @sm:px-5 @lg:px-6 @lg:py-5 @2xl:grid-cols-3 @3xl:grid-cols-4">
                      <aside className="min-w-0 border-b border-app-border pb-4 @2xl:col-span-1 @2xl:border-b-0 @2xl:border-r @2xl:pb-0 @2xl:pr-5">
                        <h3 className="mb-3 text-base font-semibold">Preview</h3>
                        <FabrikTheme
                          className="contents"
                          theme={{ primaryColor: logoColor }}
                          syncDocument={false}
                        >
                          <div className="flex min-w-0 flex-wrap items-start gap-4 @2xl:flex-col">
                            <AppBrandMark
                              tenantName={tenantName}
                              appearance={previewAppearance}
                              logo={uploadPreviewUrl ? undefined : activeLogo}
                              logoColor={logoColor}
                              className="grid size-24 place-items-center rounded-2xl bg-primary text-3xl font-semibold text-primary-foreground @sm:size-28 @sm:text-4xl @3xl:size-32"
                              imageClassName="size-full rounded-2xl object-contain"
                            />
                            <div className="min-w-0 max-w-sm flex-1 border-l border-app-border pl-4 @2xl:w-full @2xl:border-l-0 @2xl:border-t @2xl:pl-0 @2xl:pt-2">
                              <h4 className="mb-1 text-sm font-semibold">Header preview</h4>
                              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-app-border bg-app-surface-muted p-2 @sm:gap-3 @sm:p-3">
                                <AppBrandMark
                                  tenantName={tenantName}
                                  appearance={previewAppearance}
                                  logo={uploadPreviewUrl ? undefined : activeLogo}
                                  logoColor={logoColor}
                                  className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary text-sm font-semibold text-primary-foreground @sm:size-10 @sm:text-base"
                                  imageClassName="size-full rounded-lg object-contain"
                                />
                                <span className="min-w-0 break-words text-base font-medium text-app-text">
                                  {tenantName}
                                </span>
                              </div>
                            </div>
                          </div>
                        </FabrikTheme>
                      </aside>
                      <div className="flex min-w-0 flex-col gap-4 @2xl:col-span-2 @3xl:col-span-3">
                        <section className="min-w-0 rounded-xl border border-app-border p-4 @sm:p-4 @lg:p-5">
                          <h3 className="text-base font-semibold">Logo color</h3>
                          <p className="mt-1 text-sm text-app-text-secondary">
                            This color controls the logo mark and is independent of the app’s
                            primary color.
                          </p>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <CurrentColorSwatch
                              value={logoColor}
                              label="Current logo color"
                              compact
                            />
                            <Input
                              aria-label="Logo color hex value"
                              value={logoColor}
                              onChange={(event) => changeLogoColor(event.target.value)}
                              className="h-10 w-48 max-w-full min-w-0 px-3 text-sm"
                            />
                          </div>
                          <ColorPalette value={logoColor} onChange={changeLogoColor} compact />
                        </section>
                        <section className="@container min-w-0 rounded-xl border border-app-border p-4 @sm:p-4 @lg:p-5">
                          <h3 className="text-base font-semibold">Choose style</h3>
                          <p className="mt-1 text-sm text-app-text-secondary">
                            Choose how you’d like to represent your app.
                          </p>
                          <div
                            className="mt-4 grid grid-cols-3 rounded-xl border border-app-border bg-app-surface-muted p-1"
                            role="group"
                            aria-label="Logo type"
                          >
                            {(
                              [
                                ['typography', 'Typography'],
                                ['icon', 'Icons'],
                                ['custom', 'Upload your own'],
                              ] as const
                            ).map(([mode, label]) => (
                              <button
                                key={mode}
                                type="button"
                                aria-pressed={logoMode === mode}
                                onClick={() => setLogoMode(mode)}
                                className={`min-h-9 rounded-md px-1 text-xs font-medium transition-colors @sm:text-sm ${
                                  logoMode === mode
                                    ? 'bg-app-surface-card text-app-text shadow-sm'
                                    : 'text-app-text-secondary hover:text-app-text'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <div className="mt-4 border-t border-app-border pt-4">
                            {logoMode === 'typography' ? (
                              <>
                                <h4 className="text-sm font-semibold">Typography</h4>
                                <p className="mt-1 text-sm text-app-text-secondary">
                                  Use your app’s initials in a clean, modern style.
                                </p>
                                <div
                                  className="mt-3 grid grid-cols-2 gap-3 @sm:grid-cols-4"
                                  role="group"
                                  aria-label="Typography logo styles"
                                >
                                  {logoStyles.map(({ id, label }) => (
                                    <button
                                      key={id}
                                      type="button"
                                      aria-label={`${label} logo style`}
                                      aria-pressed={activeLogoStyle === id}
                                      title={label}
                                      onClick={() => {
                                        setUpload(undefined);
                                        setLogo({
                                          type: 'typography',
                                          color: logoColor,
                                          style: id,
                                        });
                                      }}
                                      className="w-fit justify-self-center rounded-xl p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    >
                                      <AppBrandMark
                                        tenantName={tenantName}
                                        appearance={previewAppearance}
                                        logo={{ type: 'typography', color: logoColor, style: id }}
                                        logoColor={logoColor}
                                        className={`grid size-12 place-items-center rounded-2xl text-xl font-semibold @sm:size-14 ${
                                          activeLogoStyle === id
                                            ? 'ring-2 ring-app-brand-strong ring-offset-2'
                                            : ''
                                        }`}
                                      />
                                    </button>
                                  ))}
                                </div>
                              </>
                            ) : logoMode === 'icon' ? (
                              <>
                                <h4 className="text-sm font-semibold">Icons</h4>
                                <p className="mt-1 text-sm text-app-text-secondary">
                                  Choose an icon to represent your app.
                                </p>
                                <Input
                                  aria-label="Search icons"
                                  placeholder="Search icons"
                                  value={logoSearch}
                                  onChange={(event) => setLogoSearch(event.target.value)}
                                  className="mt-3 h-9 text-sm"
                                />
                                <ScrollArea className="@container mt-3 h-28 min-w-0 max-w-full rounded-lg">
                                  <ScrollAreaViewport className="overflow-x-hidden overscroll-contain">
                                    <ScrollAreaContent className="grid w-full min-w-0! max-w-full grid-cols-5 gap-2 pr-2 @sm:grid-cols-8 @lg:grid-cols-12">
                                      {filteredIcons.map((iconName) => (
                                        <button
                                          key={iconName}
                                          type="button"
                                          aria-label={`${iconName} icon`}
                                          aria-pressed={
                                            activeLogo?.type === 'icon' &&
                                            activeLogo.icon === iconName
                                          }
                                          title={iconName}
                                          onClick={() => {
                                            setUpload(undefined);
                                            setLogo({
                                              type: 'icon',
                                              icon: iconName,
                                              color: logoColor,
                                            });
                                          }}
                                          className={`grid size-8 place-items-center rounded-lg border ${
                                            activeLogo?.type === 'icon' &&
                                            activeLogo.icon === iconName
                                              ? 'border-app-brand-strong ring-1 ring-app-brand-strong'
                                              : 'border-app-border hover:bg-app-surface-muted'
                                          }`}
                                        >
                                          <DynamicIcon
                                            name={iconName}
                                            className="size-5"
                                            aria-hidden="true"
                                          />
                                        </button>
                                      ))}
                                    </ScrollAreaContent>
                                  </ScrollAreaViewport>
                                  <ScrollBar />
                                </ScrollArea>
                                {filteredIcons.length === 0 && (
                                  <p className="mt-3 text-sm text-app-text-secondary">
                                    No icons found.
                                  </p>
                                )}
                              </>
                            ) : (
                              <>
                                <h4 className="text-sm font-semibold">Upload your own</h4>
                                <p className="mt-1 text-sm text-app-text-secondary">
                                  Use a square image (256 × 256 recommended), up to 1 MB. PNG, JPG,
                                  or SVG only.
                                </p>
                                <Label className="mt-3 flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-app-border text-sm hover:bg-app-surface-muted">
                                  <Upload aria-hidden="true" />
                                  {upload?.name || 'Upload image'}
                                  <Input
                                    type="file"
                                    accept="image/png,image/jpeg,image/svg+xml"
                                    className="sr-only"
                                    onChange={(event) => {
                                      const file = event.currentTarget.files?.[0];
                                      event.currentTarget.value = '';
                                      if (!file) return;
                                      setError('');
                                      setUpload(undefined);
                                      void validateFabrikAppLogoFile(file)
                                        .then(() => {
                                          setUpload(file);
                                          setLogo(undefined);
                                        })
                                        .catch((reason) => {
                                          setError(
                                            reason instanceof Error
                                              ? reason.message
                                              : 'Choose a PNG, JPG, or SVG image no larger than 1 MB.'
                                          );
                                        });
                                    }}
                                  />
                                </Label>
                              </>
                            )}
                          </div>
                        </section>
                      </div>
                    </div>
                  </ScrollAreaContent>
                </ScrollAreaViewport>
                <ScrollBar />
              </ScrollArea>
            </FabrikTheme>
            <FabrikTheme
              className="contents"
              theme={{ primaryColor: previewAppearance.primaryColor }}
              syncDocument={false}
            >
              <DialogFooter className="shrink-0 border-t border-app-border bg-app-surface-card px-4 py-3 @sm:px-5 @sm:py-4 @lg:px-6">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-app-action text-app-action-foreground hover:bg-app-action-hover disabled:bg-app-border disabled:text-app-text-faint disabled:opacity-100"
                  disabled={saving || !hexColor(logoColor)}
                  onClick={() => void saveLogo()}
                >
                  Save
                </Button>
              </DialogFooter>
            </FabrikTheme>
          </DialogContent>
        </Dialog>
      </div>
    </ToastProvider>
  );
}
